/**
 * PATH: packages/email-hub/src/feature/hooks/useEmailBackfill.resolve.ts
 * FILE_ID: EMAILHUB-FEATURE-HOOKS-BACKFILL-RESOLVE-001
 * PURPOSE: Decide whether a queued backfill chunk finished, failed, or is
 *          still running.
 * WHY: A 30-day mailbox pull often outlasts the 60s poll. That is a live
 *      worker, not a timeout, and must not be written to Recent issues.
 */
import { fetchEmailSyncStatus } from './useEmails'

/** Poll cadence. Total budget is about 62s. */
export const QUEUED_POLL_DELAYS_MS = [4_000, 4_000, 6_000, 8_000, 10_000, 14_000, 16_000] as const

export interface QueuedResolution {
  status: 'success' | 'partial' | 'error' | 'timeout' | 'running'
  created: number
  fetched: number
  error_message: string | null
  waited_ms: number
}

interface SyncStatusRow {
  status?: string
  started_at?: string | null
  emails_synced?: number
  emails_found?: number
  error_message?: string | null
}

/** One poll row -> keep waiting, a terminal outcome, or ignore a stale row. */
export function classifyQueuedRow(
  row: SyncStatusRow | null | undefined,
  firedAt: string,
): 'ignore' | 'wait' | 'success' | 'partial' | 'error' {
  if (!row) return 'ignore'
  const started = row.started_at ?? null
  if (started && started < firedAt) return 'ignore'
  if (row.status === 'success') return 'success'
  if (row.status === 'partial') return 'partial'
  if (row.status === 'error') return 'error'
  if (row.status === 'running') return 'wait'
  return 'ignore'
}

export async function resolveQueuedChunk(firedAt: string): Promise<QueuedResolution> {
  const startedAt = Date.now()
  let sawRunning = false
  for (const delay of QUEUED_POLL_DELAYS_MS) {
    await new Promise(resolve => window.setTimeout(resolve, delay))
    const status = await fetchEmailSyncStatus(firedAt)
    const verdict = classifyQueuedRow(status?.latest, firedAt)
    if (verdict === 'ignore') continue
    if (verdict === 'wait') {
      sawRunning = true
      continue
    }
    const row = status?.latest
    return {
      status: verdict,
      created: row?.emails_synced || 0,
      fetched: row?.emails_found || 0,
      error_message: verdict === 'error' ? (row?.error_message ?? null) : null,
      waited_ms: Date.now() - startedAt,
    }
  }
  return {
    status: sawRunning ? 'running' : 'timeout',
    created: 0,
    fetched: 0,
    error_message: null,
    waited_ms: Date.now() - startedAt,
  }
}
